import {
  VerificationQueryStatus,
  VerificationStatus,
} from 'src/app/api/autogenerated';

import { FilePurpose } from 'src/app/types/FileTypes';
import {
  OnboardingDocumentStatus,
  OnboardingQueueStatus,
  OnboardingStatusDisplay,
  OnboardingVerificationQuery,
} from './admin.types';

export const BUSINESS_PROOF_UPLOAD_TYPES = [
  'licence',
  'udyam',
  'registrationTax',
  'utility',
  'gst',
  'iec',
  'oldInvoice',
  'oldFira',
  'deed',
  'contractAgreement',
] as const;

export type BusinessProofUploadType =
  (typeof BUSINESS_PROOF_UPLOAD_TYPES)[number];

export interface BusinessProofUploadOption {
  type: BusinessProofUploadType;
  label: string;
}

export const MULTI_DOCUMENT_UPLOAD_REQUIRED_COUNT = 2;

export const BUSINESS_PROOF_UPLOAD_OPTIONS: BusinessProofUploadOption[] = [
  {
    type: 'licence',
    label:
      'Certificate/licence issued by the municipal authorities under the Shop and Establishment Act',
  },

  {
    type: 'udyam',
    label: 'Udyam Certificate (all three pages)',
  },

  {
    type: 'registrationTax',
    label:
      'Certificate/registration document issued by Sales Tax/Service Tax/Professional Tax authorities (within last 12 months)',
  },

  {
    type: 'utility',
    label:
      'Utility bills such as electricity, water, landline telephone bills, etc., in the trade name of the business (within the last three months)',
  },

  {
    type: 'gst',
    label: 'GST document',
  },

  {
    type: 'iec',
    label: 'IEC document',
  },

  {
    type: 'oldInvoice',
    label: 'Old Invoice',
  },

  {
    type: 'oldFira',
    label: 'Old Fira',
  },

  {
    type: 'deed',
    label: 'Deed document',
  },

  {
    type: 'contractAgreement',
    label: 'Contract or Franchise Agreement',
  },
];

export interface QueryRequirementBadge {
  label: string;
  variant: 'single' | 'any-one' | 'any-two';
}

interface QueryUploadTypeRule {
  keywords: string[];
  uploadType: string;
}

const QUERY_UPLOAD_TYPE_RULES: QueryUploadTypeRule[] = [
  {
    keywords: [
      'shop establishment',
      'shop and establishment',
      'shop act',
      'establishment certificate',
    ],
    uploadType: 'licence',
  },

  {
    keywords: [
      'utility bill',
      'utility',
      'telephone',
      'landline',
      'water',
      'electricity',
    ],
    uploadType: 'utility',
  },

  {
    keywords: ['municipal authority', 'municipal'],
    uploadType: 'licence',
  },

  {
    keywords: [
      'sales tax',
      'service tax',
      'professional tax',
      'professional certificate',
      'professional',
    ],
    uploadType: 'registrationTax',
  },

  { keywords: ['udyam', 'udyam certificate'], uploadType: 'udyam' },

  { keywords: ['gst document', 'gstin', 'gst certificate'], uploadType: 'gst' },

  {
    keywords: ['iec document', 'iec certificate', 'import export code'],
    uploadType: 'iec',
  },

  { keywords: ['firc', 'fira', 'old fira'], uploadType: 'oldFira' },

  { keywords: ['old invoice'], uploadType: 'oldInvoice' },

  { keywords: ['invoice'], uploadType: 'oldInvoice' },

  {
    keywords: [
      'shareholder register',
      'share certificate',
      'deed document',
      'deed',
    ],
    uploadType: 'deed',
  },

  {
    keywords: [
      'contract or franchise agreement',
      'franchise agreement',
      'contract agreement',
      'franchise',
    ],
    uploadType: 'contractAgreement',
  },

  {
    keywords: ['passport', 'photo id', 'national id', 'director'],
    uploadType: 'oldInvoice',
  },

  {
    keywords: ['pan'],
    uploadType: 'registrationTax',
  },
];

export function getMatchedUploadTypes(searchText: string): string[] {
  const matchedTypes = QUERY_UPLOAD_TYPE_RULES.filter((rule) =>
    rule.keywords.some((keyword) => searchText.includes(keyword)),
  ).map((rule) => rule.uploadType);

  return [...new Set(matchedTypes)];
}

function getDocumentOptionCount(searchText: string): number {
  const matchedTypeCount = getMatchedUploadTypes(searchText).length;
  const parsedOptionCount = countMessageDocumentOptions(searchText);

  if (matchedTypeCount >= 2) {
    return Math.max(matchedTypeCount, parsedOptionCount);
  }

  return parsedOptionCount;
}

export function shouldShowTwoOfFourUploadUI(
  _query: OnboardingVerificationQuery,
): boolean {
  return true;
}

export function getMultiDocumentUploadOptionCount(): number {
  return BUSINESS_PROOF_UPLOAD_OPTIONS.length;
}

export function getUploadTypeFilePurpose(uploadType: string): FilePurpose {
  switch (uploadType) {
    case 'udyam':
      return FilePurpose.KYB_DOC_UDYAM_CERTIFICATE;
    case 'licence':
      return FilePurpose.KYB_DOC_CERTIFICATE_ISSUED_BY_MUNICIPAL_AUTHORITY;
    case 'utility':
      return FilePurpose.KYB_DOC_UTILITY_BILL;
    case 'registrationTax':
      return FilePurpose.KYB_DOC_CERTIFICATE_ISSUED_BY_TAX_AUTHORITY;
    case 'gst':
      return FilePurpose.GST_IN_DOCUMENT;
    case 'iec':
      return FilePurpose.IEC_DOCUMENT;
    case 'oldFira':
      return FilePurpose.KYB_DOC_OLD_FIRC;
    case 'oldInvoice':
      return FilePurpose.KYB_DOC_OLD_INVOICE;
    case 'deed':
      return FilePurpose.DEED_DOCUMENT;
    case 'contractAgreement':
      return FilePurpose.KYB_DOC_CONTRACT_AGREEMENT;
    default:
      return FilePurpose.KYB_DOC_UTILITY_BILL;
  }
}

function requiresMultipleUploads(searchText: string): boolean {
  return (
    isTwoOfFourRequired(searchText) ||
    searchText.includes('at least two') ||
    searchText.includes('any two') ||
    searchText.includes('any combination')
  );
}

export function mapVerificationStatus(
  status: VerificationStatus,
): OnboardingQueueStatus {
  switch (status) {
    case 'INPUT_REQUIRED':
      return 'input_required';
    case 'APPROVED':
      return 'approved';
    case 'REJECTED':
      return 'rejected';
    case 'UNDER_REVIEW':
    case 'IN_PROGRESS':
    case 'PENDING':
    default:
      return 'in_review';
  }
}

export const ONBOARDING_STATUS_MAP: Record<
  OnboardingQueueStatus,
  OnboardingStatusDisplay
> = {
  input_required: {
    label: 'Input Required',
    bgClass: 'bg-light-warning',
    textClass: 'text-warning',
  },

  in_review: {
    label: 'In Review',
    bgClass: 'bg-light-primary',
    textClass: 'text-primary',
  },

  approved: {
    label: 'Approved',
    bgClass: 'bg-light-success',
    textClass: 'text-success',
  },

  rejected: {
    label: 'Rejected',
    bgClass: 'bg-light-error',
    textClass: 'text-error',
  },
};

export const DOCUMENT_STATUS_MAP: Record<
  OnboardingDocumentStatus,
  OnboardingStatusDisplay
> = {
  verified: {
    label: 'Verified',
    bgClass: 'bg-light-success',
    textClass: 'text-success',
  },

  under_review: {
    label: 'Under Review',
    bgClass: 'bg-light-primary',
    textClass: 'text-primary',
  },

  pending: {
    label: 'Pending',
    bgClass: 'bg-light-warning',
    textClass: 'text-warning',
  },
};

export const QUERY_STATUS_MAP: Record<
  VerificationQueryStatus,
  OnboardingStatusDisplay
> = {
  PENDING: {
    label: 'Pending',
    bgClass: 'bg-light-warning',
    textClass: 'text-warning',
  },

  SUBMITTED: {
    label: 'Submitted',
    bgClass: 'bg-light-primary',
    textClass: 'text-primary',
  },

  FAILED: {
    label: 'Failed',
    bgClass: 'bg-light-error',
    textClass: 'text-error',
  },
};

function getQuerySearchText(query: OnboardingVerificationQuery): string {
  return `${query.title} ${query.description}`.toLowerCase();
}

function isTwoOfFourRequired(searchText: string): boolean {
  return (
    /(?:upload|submit|provide)\s+2\s+of(?:\s+the\s+following)?\s+4/.test(
      searchText,
    ) ||
    /\b2\s+of\s+(?:the\s+)?following\s+4\b/.test(searchText) ||
    /\bany\s+2\s+of\s+4\b/.test(searchText) ||
    /\b2\s+of\s+4\b/.test(searchText) ||
    /any\s+2\s+of\s+\d+/.test(searchText)
  );
}

function hasAllBusinessProofKeywords(searchText: string): boolean {
  const hasUdyam = searchText.includes('udyam');
  const hasLicence =
    searchText.includes('shop establishment') ||
    searchText.includes('shop and establishment') ||
    searchText.includes('shop act');

  const hasRegistrationTax =
    searchText.includes('professional') ||
    searchText.includes('sales tax') ||
    searchText.includes('service tax') ||
    searchText.includes('professional tax');

  const hasUtility =
    searchText.includes('utility bill') || searchText.includes('utility');

  return hasUdyam && hasLicence && hasRegistrationTax && hasUtility;
}

function countMessageDocumentOptions(message: string): number {
  const optionsSection = message.includes(':')
    ? (message.split(':').pop() ?? message)
    : message;

  const options = optionsSection
    .split(/,|\bor\b/)
    .map((option) => option.trim())
    .filter((option) => option.length > 0);

  return options.length;
}

export function getQueryRequirementBadge(
  query: OnboardingVerificationQuery,
): QueryRequirementBadge {
  if (shouldShowTwoOfFourUploadUI(query)) {
    return {
      label: 'Document Upload',
      variant: 'single',
    };
  }

  const searchText = getQuerySearchText(query);

  const anyTwoOfMatch = searchText.match(/any\s+2\s+of\s+(\d+)/);
  if (anyTwoOfMatch) {
    return {
      label: `Any 2 of ${anyTwoOfMatch[1]}`,
      variant: 'any-two',
    };
  }

  const anyOneOfMatch = searchText.match(/any\s+1\s+of\s+(\d+)/);
  if (anyOneOfMatch) {
    return {
      label: `Any 1 of ${anyOneOfMatch[1]}`,
      variant: 'any-one',
    };
  }

  if (
    searchText.includes('at least two') ||
    searchText.includes('any combination')
  ) {
    const optionCount = Math.max(getDocumentOptionCount(searchText), 2);
    return {
      label: `Any 2 of ${optionCount}`,
      variant: 'any-two',
    };
  }

  const matchedUploadTypes = getMatchedUploadTypes(searchText);

  if (matchedUploadTypes.length >= 2) {
    const optionCount = getDocumentOptionCount(searchText);

    if (requiresMultipleUploads(searchText)) {
      return {
        label: `Any 2 of ${optionCount}`,
        variant: 'any-two',
      };
    }

    return {
      label: `Any 1 of ${optionCount}`,
      variant: 'any-one',
    };
  }

  if (
    searchText.includes(' we accept ') ||
    (searchText.includes(' or ') &&
      (searchText.includes('passport') ||
        searchText.includes('id card') ||
        searchText.includes('national id')))
  ) {
    return { label: 'Any 1 of 2', variant: 'any-one' };
  }

  if (
    searchText.includes('ubo') ||
    searchText.includes('ultimate beneficial') ||
    searchText.includes('share certificate')
  ) {
    const optionCount = Math.max(countMessageDocumentOptions(searchText), 4);
    return {
      label: `Any 2 of ${optionCount}`,
      variant: 'any-two',
    };
  }

  return { label: 'Single Upload', variant: 'single' };
}

export function isQueryFulfilled(query: OnboardingVerificationQuery): boolean {
  return query.status === 'SUBMITTED';
}

export function getQueryUploadStatusLabel(
  query: OnboardingVerificationQuery,
): string {
  if (query.status === 'SUBMITTED') {
    return 'File uploaded';
  }

  if (query.status === 'FAILED') {
    return 'Upload failed — please retry';
  }

  return 'No file uploaded yet';
}

export function getQueryUploadType(query: OnboardingVerificationQuery): string {
  const searchText = getQuerySearchText(query);
  const matchedTypes = getMatchedUploadTypes(searchText);

  if (matchedTypes.length > 0) {
    return matchedTypes[0];
  }
  return 'utility';
}

export function getQueryAllowMultiple(
  query: OnboardingVerificationQuery,
): boolean {
  return (
    shouldShowTwoOfFourUploadUI(query) ||
    getQueryRequirementBadge(query).variant === 'any-two'
  );
}
